/**
 * The draft as the wire takes it — the ONE module that knows a PATCH carries no metrics.
 *
 * ── Metrics are frozen once the exercise exists, and the type system says so ────────────────
 * Library requirement R11: the measurement fields are fixed at creation and no edit path changes
 * them, because logged sets were validated against that list and record eligibility follows it.
 * `PatchExerciseRequest` has no `measurementFields` member at all, and `editExerciseRequest` builds
 * its body as a fresh literal typed `PatchExerciseRequest`, so adding them there is an
 * excess-property compile error. The wizard draws the rows read-only on an edit and says why; this is the second half.
 *
 * The exercise TYPE stays editable on both paths. It is a taxonomy label rather than a data
 * contract (R11 again), and changing it after creation does not re-apply its default metrics — the
 * defaults are a creation-time convenience, and `StepBasics` does not offer the tiles as a way to
 * rewrite a frozen list.
 *
 * ── What is trimmed, dropped and de-duplicated before it goes ───────────────────────────────
 * · Blank instruction rows are dropped. The steps editor opens a row when „Dodaj krok" is pressed
 *   and a trainer who changes their mind leaves it empty; sending it would put a numbered blank
 *   line in every client's app.
 * · Links go through the ENTITY's policy, which is the browser's mirror of `ExternalLinkPolicy`.
 *   Only `accepted` rows are sent, duplicates already collapsed — so three links always mean three
 *   distinct destinations (R12), exactly as the server counts them.
 * · An empty string is sent as `''` rather than as `null` on a PATCH: `null` means "leave alone"
 *   there, and a trainer who cleared the description means it to be cleared.
 *
 * ── `[]` is not `null` on a PATCH, and the difference is the whole point ────────────────────
 * The contract reads `null` as "unchanged" and `[]` as "clear it". The wizard always shows every
 * list it submits, so it always states them: a movement whose equipment the trainer removed sends
 * `equipmentIds: []` and comes back with none. Sending `null` for an empty list would make removal
 * the one edit this screen cannot perform.
 */

import { checkExerciseLinks, muscleGroupToApi } from '~/entities/exercise-library';
import { exerciseTypeToApi } from '~/entities/exercise-library';
import { measurementFieldToWire } from '~/shared/measurement';
import type {
  TrainWithMeModulesExercisesDtosRequestsPatchExerciseRequest,
  TrainWithMeModulesExercisesDtosRequestsPostExerciseRequest,
} from '~/shared/api/generated/model';

import type { ExerciseDraft } from './exerciseDraft';

type PostBody = TrainWithMeModulesExercisesDtosRequestsPostExerciseRequest;
type PatchBody = TrainWithMeModulesExercisesDtosRequestsPatchExerciseRequest;

/** The links a request states: accepted rows only, duplicates collapsed, blank rows dropped. */
function acceptedLinks(draft: ExerciseDraft) {
  const typed = draft.links.filter((link) => link.url.trim() !== '' || link.label.trim() !== '');
  return checkExerciseLinks(typed).accepted.map((link) => ({ url: link.url, label: link.label }));
}

/** `POST /exercise`. The one path that states what the movement measures. */
export function createExerciseRequest(draft: ExerciseDraft): PostBody {
  return {
    name: draft.name.trim(),
    exerciseType: exerciseTypeToApi(draft.type),
    description: draft.description.trim(),
    instructions: instructionsOf(draft),
    primaryMuscleGroups: draft.primary.map(muscleGroupToApi),
    secondaryMuscleGroups: draft.secondary.map(muscleGroupToApi),
    equipmentIds: [...draft.equipmentIds],
    links: acceptedLinks(draft),
    // Empty is legal and is exactly right for a mobility movement (measurements R2). Nothing is
    // substituted for it — the defect that made `/complete` unclosable was a substituted default.
    measurementFields: draft.metrics.fields.map(measurementFieldToWire),
    // Stated only when true: a movement the trainer authored from scratch sends the body it always
    // did, and an older server that does not know the field is never sent it for one.
    ...(draft.fromProposedExercise ? { fromProposedExercise: true } : {}),
  };
}

/**
 * `PATCH /exercise/{id}`. No metrics — written as a fresh literal typed `PatchBody`, so adding
 * `measurementFields` here is an excess-property error, not a convention.
 *
 * `links` and `exerciseType` are `null` ("unchanged") unless the trainer touched them in this
 * sitting. See `ExerciseDraft.linksEdited`: the links on screen are the read-FILTERED list, and
 * re-sending them would silently delete a stored link the read path refuses; and a `custom` movement
 * opens on the Siłowe tile, so re-sending the type would relabel it without the trainer choosing to.
 */
export function editExerciseRequest(draft: ExerciseDraft): PatchBody {
  const body: PatchBody = {
    name: draft.name.trim(),
    exerciseType: draft.typeEdited ? exerciseTypeToApi(draft.type) : null,
    description: draft.description.trim(),
    instructions: instructionsOf(draft),
    primaryMuscleGroups: draft.primary.map(muscleGroupToApi),
    secondaryMuscleGroups: draft.secondary.map(muscleGroupToApi),
    equipmentIds: [...draft.equipmentIds],
    links: draft.linksEdited ? acceptedLinks(draft) : null,
  };
  return body;
}

function instructionsOf(draft: ExerciseDraft): string[] {
  return draft.instructions.map((step) => step.trim()).filter((step) => step !== '');
}
